/**
 * Post-login bootstrap hook
 * 
 * Runs after successful login to prefetch key data (Clients, Leads, Agents)
 * in parallel. Returns `isReady` when all bootstrap operations complete.
 * 
 * This ensures that when the user navigates to Clients/Leads/Agents pages,
 * the data is already in cache and pages appear instantly.
 * 
 * Usage:
 * ```tsx
 * const { company } = useParams<{ company: string }>();
 * const { client } = useClient();
 * 
 * const { isReady } = usePostLoginBootstrap({
 *   companySlug: company,
 *   enabled: !!client && !!company,
 * });
 * ```
 */

import { useEffect, useState } from "react";
import { useQueryClient, QueryClient } from "@tanstack/react-query";
import { clientKeys } from "../lib/queryKeys";
import { leadKeys } from "../lib/queryKeys";
import { agentKeys } from "../lib/queryKeys";
import { getAdminClients } from "../api/clientsApi";
import rpcLeads from "../api/rpc/rpcLeads";
import { getAgents } from "../api/agentsApi";
import { DEFAULT_CLIENTS_QUERY_PARAMS } from "./clientsQueryDefaults";
import { DEFAULT_LEADS_QUERY_PARAMS } from "./leadsQueryDefaults";
import { DEFAULT_AGENT_QUERY_PARAMS } from "./agentQueryDefaults";

type UsePostLoginBootstrapOptions = {
  companySlug: string | undefined;
  enabled?: boolean; // default true
};

/**
 * Helper to ensure data is prefetched (only if not already in cache)
 */
async function ensurePrefetched<TData>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  queryFn: () => Promise<TData>
): Promise<TData | undefined> {
  const existing = queryClient.getQueryData<TData>(queryKey);
  if (existing) {
    return existing;
  }

  try {
    const data = await queryClient.prefetchQuery({
      queryKey,
      queryFn,
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 60 * 60 * 1000, // 60 minutes
    });
    return queryClient.getQueryData<TData>(queryKey);
  } catch (error) {
    return undefined;
  }
}

export function usePostLoginBootstrap({
  companySlug,
  enabled = true,
}: UsePostLoginBootstrapOptions) {
  const queryClient = useQueryClient();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (!companySlug) return;

    let cancelled = false;

    async function run() {
      try {
        // Build default params for each entity
        const clientParams = { ...DEFAULT_CLIENTS_QUERY_PARAMS };
        const leadsParams = { ...DEFAULT_LEADS_QUERY_PARAMS };
        const agentParams = { ...DEFAULT_AGENT_QUERY_PARAMS };

        // Prefetch in parallel
        const [clientsResult, leadsResult, agentsResult] = await Promise.allSettled([
          // Clients
          ensurePrefetched(
            queryClient,
            clientKeys.list(companySlug!, clientParams),
            async () => {
              const response = await getAdminClients(companySlug!, clientParams);
              
              // Handle different response formats (same as useClientsQuery)
              if (Array.isArray(response)) {
                return {
                  data: response,
                  pagination: {
                    page: 1,
                    limit: response.length,
                    total: response.length,
                    pages: 1,
                    hasMore: false,
                  },
                };
              }

              if (response.data && Array.isArray(response.data)) {
                return {
                  data: response.data,
                  pagination: response.pagination,
                  cursor: response.cursor,
                };
              }

              return {
                data: [],
                pagination: {
                  page: 1,
                  limit: clientParams.limit || 50,
                  total: 0,
                  pages: 0,
                  hasMore: false,
                },
              };
            }
          ),

          // Leads
          ensurePrefetched(
            queryClient,
            leadKeys.list(companySlug!, leadsParams),
            async () => {
              const res = await rpcLeads.list({
                q: leadsParams.search || undefined,
                status: leadsParams.status,
                managerId: leadsParams.managerId,
                rating: leadsParams.rating,
                source: leadsParams.source,
                leadNum: leadsParams.leadNum,
                firstName: leadsParams.firstName,
                page: leadsParams.page,
                limit: leadsParams.limit,
              });

              if (!res.data?.ok) {
                throw new Error(res.data?.error || 'Failed to get leads');
              }

              const leadsData = res.data.data || {};
              const items = Array.isArray(leadsData?.items) ? leadsData.items : [];
              const total = Number(leadsData?.total || items.length);

              return {
                data: items,
                pagination: {
                  page: leadsParams.page || 1,
                  limit: leadsParams.limit || 25,
                  total,
                  pages: Math.ceil(total / (leadsParams.limit || 25)),
                  hasMore: (leadsParams.page || 1) * (leadsParams.limit || 25) < total,
                },
              };
            }
          ),

          // Agents
          ensurePrefetched(
            queryClient,
            agentKeys.list(companySlug!, agentParams),
            async () => {
              const queryParams: any = {
                page: agentParams.page || 1,
                limit: agentParams.limit || 25,
              };
              if (agentParams.search) queryParams.search = agentParams.search;
              if (agentParams.status) queryParams.status = agentParams.status;
              if (agentParams.role) queryParams.role = agentParams.role;
              if (agentParams.sort) queryParams.sort = agentParams.sort;

              const result = await getAgents(companySlug!, queryParams);
              const agentsArray = Array.isArray(result.agents) ? result.agents : (Array.isArray(result) ? result : []);
              const total = result.total !== undefined ? result.total : agentsArray.length;

              return {
                agents: agentsArray,
                pagination: {
                  page: agentParams.page || 1,
                  limit: agentParams.limit || 25,
                  total,
                  pages: Math.ceil(total / (agentParams.limit || 25)),
                  hasMore: (agentParams.page || 1) * (agentParams.limit || 25) < total,
                },
              };
            }
          ),
        ]);

        // Mark as ready even if some prefetches failed (don't block user forever)
        if (!cancelled) {
          setIsReady(true);
        }
      } catch (error) {
        // In case of error, still mark ready so the user is not stuck forever
        if (!cancelled) {
          setIsReady(true);
        }
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [companySlug, enabled, queryClient]);

  return { isReady };
}

